import React from 'react';
import { UserOutlined } from '@ant-design/icons';
import { clientLoginPath } from '@api/http.api.ts';
// import HelpCircleIcon from '@assets/icons/help-circle.svg';
import LogOutIcon from '@assets/icons/log-out.svg';
import PersonIcon from '@assets/icons/person.svg';
import RibbonIcon from '@assets/icons/ribbon.svg';
import SettingsIcon from '@assets/icons/settings.svg';
import { BaseTooltip } from '@components';
import { ROOT_TOKENS_COLORS } from '@constants/colors.ts';
import { DATE_FORMATS } from '@constants/date.ts';
import { SIZES } from '@constants/fonts.ts';
import { TEST_IDS } from '@constants/testIds.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import { AboutVersionIcon, AboutVersionWrapper, VersionWrapper } from '@layouts/main/sider/menu/styled.ts';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import { TooltipContent } from '@routes/___shared___/styled.tsx';
import { doLogout } from '@routes/auth/___shared___/auth.slice.ts';
import { cancelTokenRefreshTimer } from '@routes/auth/___shared___/authTokenRefreshScheduler.ts';
import { formatDateBasedOnPreferences } from '@utils/date.ts';

import { AvatarImg } from '../../___shared___/components/avatar';
import {
    APP_VERSION_MENU_ITEM_ID,
    Avatar,
    InfoSection,
    InfoWrapper,
    LinkItem,
    LinkItemIcon,
    LinkItemName,
    LinksSection,
    Name,
    ProfileMenuWrapper,
    Role,
} from './styled.tsx';

interface LinkItem {
    icon: React.ReactNode;
    name: string;
    url?: string;
    action?: () => void;
    id?: string;
}

interface Props {
    name: string;
    role: string;
    avatarUrl: string;
    closeMenu: () => void;
}

const ProfileMenu: React.FC<Props> = ({ name, role, avatarUrl, closeMenu }) => {
    const { t } = useLingui();
    const navigate = useCustomNavigate();
    const dispatch = useAppDispatch();

    const healthStatus = useAppSelector((state) => state.health.healthStatus);
    const buildTimeUTC = healthStatus?.buildTimeUTC
        ? formatDateBasedOnPreferences(healthStatus?.buildTimeUTC, DATE_FORMATS.EUROPEAN_DASH)
        : '';
    const deployedAtUTC = healthStatus?.buildTimeUTC
        ? formatDateBasedOnPreferences(healthStatus?.deployedAtUTC, DATE_FORMATS.EUROPEAN_DASH)
        : '';

    const handleLogout = () => {
        dispatch(doLogout())
            .then(() => {
                cancelTokenRefreshTimer();
                window.location.replace(clientLoginPath);
            })
            .catch(() => {});
    };

    const links = [
        { icon: <PersonIcon />, name: t`My Profile`, url: ROUTES.PROFILE_BAR.MY_PROFILE },
        { icon: <RibbonIcon />, name: t`Results`, url: ROUTES.PROFILE_BAR.RESULTS },
        { icon: <SettingsIcon />, name: t`Account Settings`, url: ROUTES.PROFILE_BAR.ACCOUNT_SETTINGS },
        // TODO: temporarily hidden - BBL-2341
        // { icon: <HelpCircleIcon />, name: t`Help Center`, url: ROUTES.PROFILE_BAR.HELP_CENTER },
        { icon: <AboutVersionIcon />, name: t`Version`, url: '', action: () => {}, id: APP_VERSION_MENU_ITEM_ID },
        {
            icon: <LogOutIcon />,
            name: t`Log Out`,
            url: ROUTES.HOME,
            action: handleLogout,
        },
    ] as LinkItem[];

    return (
        <ProfileMenuWrapper>
            <InfoSection>
                <Avatar>{avatarUrl ? <AvatarImg avatarURI={avatarUrl} /> : <UserOutlined />}</Avatar>
                <InfoWrapper>
                    <Name>{name}</Name>
                    <Role>{role}</Role>
                </InfoWrapper>
            </InfoSection>
            <LinksSection>
                {links.map((link) => (
                    <LinkItem
                        data-cy={TEST_IDS.PROFILE.profileMenuLink(link.name)}
                        key={link.name}
                        onClick={() => {
                            if (link.action) link.action();
                            else if (link.url) navigate(link.url);
                            closeMenu();
                        }}
                        id={link.id}
                    >
                        <LinkItemIcon>{link.icon}</LinkItemIcon>
                        <LinkItemName>{link.name}</LinkItemName>

                        {!!(link.name === t`Version` && healthStatus?.version) && (
                            <AboutVersionWrapper onClick={(e) => e.stopPropagation()}>
                                <BaseTooltip
                                    arrow={false}
                                    title={
                                        <TooltipContent>
                                            {t`Built on:`} <strong>{buildTimeUTC}</strong> <br />
                                            {t`Deployed on:`}
                                            <strong>{deployedAtUTC}</strong>
                                        </TooltipContent>
                                    }
                                    placement="bottomLeft"
                                    color={ROOT_TOKENS_COLORS.white.default}
                                >
                                    <VersionWrapper
                                        size={SIZES.BODY_SM_MED}
                                        copyable={{
                                            text: healthStatus.version,
                                            tooltips: [t`Copy app version`, t`Copied`],
                                        }}
                                        ellipsis
                                    >
                                        {healthStatus.version}
                                    </VersionWrapper>
                                </BaseTooltip>
                            </AboutVersionWrapper>
                        )}
                    </LinkItem>
                ))}
            </LinksSection>
        </ProfileMenuWrapper>
    );
};

export default ProfileMenu;
